'use client';

import { useState, useEffect, useRef } from 'react';
import { Button } from 'src/components/ui/button';
import { Switch } from 'src/components/ui/switch';
import { Checkbox } from 'src/components/ui/checkbox';
import { Separator } from 'src/components/ui/separator';
import { Info } from 'lucide-react';
import { Label } from 'src/components/ui/label';
import { Card, CardDescription, CardFooter, CardHeader, CardTitle } from 'src/components/ui/card';
import { useSelector } from 'src/redux/store';
import { selectLatestSubscription, StripeSubscriptionStatus } from 'src/redux/slices/stripeSlice';
import SimplyReviewIcon from 'src/assets/Logo/SimplyReviewIcon';
import { LEADS, REVIEW, LABS } from 'src/theme/palette';
import SimplyLeadsIcon from 'src/assets/Logo/SimplyLeadsIcon';
import { Tooltip, TooltipContent } from '../ui/tooltip';
import { TooltipTrigger } from '@radix-ui/react-tooltip';
import { httpsCallable } from 'firebase/functions';
import { Functions } from 'src/contexts/FirebaseContext';
import { toast } from 'sonner';
import { useCustomer } from 'src/hooks/useUserActivity';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from 'src/components/ui/dialog';
import { Alert } from '../ui/alert';
import { PriceId, PriceIdType } from 'src/enum';

const simplyReview = 'prod_NOegOOdZknsZZg';
const simplyLeads = 'prod_NjJ5MbiAcNrN5D';
const simplyBundle = 'prod_Od5zQsOx6T54Vq';

const validProducts = [
  simplyReview,
  simplyLeads,
  // simplyBundle
];

const validLeadPriceIds: Partial<PriceIdType[]> = [
  PriceId.SIMPLY_LEADS_MONTHLY_EARLYBIRD,
  PriceId.SIMPLY_LEADS_YEARLY,
];

const validReviewPriceIds: Partial<PriceIdType[]> = [
  PriceId.SIMPLY_REVIEW_MONTHLY,
  PriceId.SIMPLY_REVIEW_YEARLY,
];

const validBundlePriceIds: Partial<PriceIdType[]> = [
  PriceId.SIMPLY_BUNDLE_MONTHLY,
  PriceId.SIMPLY_BUNDLE_YEARLY,
];

// const validPriceIds = [...validReviewPriceIds, ...validLeadPriceIds];

// type PriceIdType =
//   | typeof PriceId.SIMPLY_REVIEW_MONTHLY
//   | typeof PriceId.SIMPLY_REVIEW_YEARLY
//   | typeof PriceId.SIMPLY_LEADS_MONTHLY_EARLYBIRD
//   | typeof PriceId.SIMPLY_LEADS_YEARLY;

export default function StripeSubscription() {
  const userId = useSelector((state) => state.user.id);
  const { customer } = useCustomer(userId);
  const subscription = useSelector(selectLatestSubscription);
  const [isYearly, setIsYearly] = useState(false);
  const [products, setProducts] = useState({
    review: false,
    leads: false,
    bundle: false,
  });
  const [prorationDetails, setProrationDetails] = useState<any>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isOpeningPortal, setIsOpeningPortal] = useState(false);

  const handleProductChange = (product: 'review' | 'leads' | 'bundle') => {
    setProducts((prev) => ({ ...prev, [product]: !prev[product] }));
  };
  console.log({ subscription });

  const reviewPrice = isYearly ? 210 : 21;
  const leadsPrice = isYearly ? 168 : 9;
  const bundlePrice = isYearly ? 590 : 49;

  const hasBundleProduct =
    subscription?.items?.some((item) => item.plan.product === simplyBundle) &&
    (subscription.status === StripeSubscriptionStatus.Active ||
      subscription.status === StripeSubscriptionStatus.IncompleteExpired);

  const subtotal =
    (products.review ? reviewPrice : 0) +
    (products.leads ? leadsPrice : 0) +
    (products.bundle ? bundlePrice : 0);

  const [hasChanges, setHasChanges] = useState(false);
  const initialState = useRef({ isYearly, products });

  // Initialize state from current subscription when component mounts or subscription changes
  useEffect(() => {
    console.log('subscription', subscription);
    if (!subscription?.id) return;
    const subItems = subscription.items || [];
    const reviewSub = subItems.find((item) =>
      validReviewPriceIds.includes(item.price.id as PriceIdType)
    );
    const leadsSub = subItems.find((item) =>
      validLeadPriceIds.includes(item.price.id as PriceIdType)
    );

    const bundleSub = subItems.find((item) =>
      validBundlePriceIds.includes(item.price.id as PriceIdType)
    );

    const currentProducts = { review: !!reviewSub, leads: !!leadsSub, bundle: !!bundleSub };
    const currentIsYearly =
      reviewSub?.price.id === PriceId.SIMPLY_REVIEW_YEARLY ||
      leadsSub?.price.id === PriceId.SIMPLY_LEADS_YEARLY ||
      bundleSub?.price.id === PriceId.SIMPLY_BUNDLE_YEARLY;
    if (subscription.status !== StripeSubscriptionStatus.Canceled) {
      setProducts(currentProducts);
      setIsYearly(currentIsYearly);
      initialState.current = { isYearly: currentIsYearly, products: currentProducts };
    }
    console.log('Subscription initialized:', { currentProducts, currentIsYearly });
  }, [subscription?.id]);

  // Track changes between UI state and original subscription
  useEffect(() => {
    const yearlyChanged = isYearly !== initialState.current.isYearly;
    const reviewChanged = products.review !== initialState.current.products.review;
    const leadsChanged = products.leads !== initialState.current.products.leads;
    const bundleChanged = products.bundle !== initialState.current.products.bundle;

    setHasChanges(yearlyChanged || reviewChanged || leadsChanged || bundleChanged);
  }, [isYearly, products]);

  const hasProductsSelected = products.review || products.leads || products.bundle;

  const getProrationPreview = async () => {
    if (!subscription) return;
    setIsUpdating(true);

    // Calculate what has changed
    const yearlyChanged = isYearly !== initialState.current.isYearly;
    const reviewChanged = products.review !== initialState.current.products.review;
    const leadsChanged = products.leads !== initialState.current.products.leads;
    // const bundleChanged = products.bundle !== initialState.current.products.bundle;

    // Check if there are any changes
    const hasAnyChange = yearlyChanged || reviewChanged || leadsChanged;
    if (!hasAnyChange) {
      toast.info('No changes detected.');
      return;
    }

    // Prepare parameters for the proration preview
    const subItems = subscription.items || [];

    // Find all the changes we need to make
    const changes: Array<{ addPriceId?: string; removeItemId?: string }> = [];

    // Handle product changes first
    const reviewItem = subItems.find((item: any) => validReviewPriceIds.includes(item.price.id));

    const leadsItem = subItems.find((item: any) => validLeadPriceIds.includes(item.price.id));

    // const bundleItem = subItems.find((item: any) =>
    //   [bundleMonthlyPrice, bundleYearlyPrice].includes(item.price.id)
    // );

    // If billing cycle changed, we need to update ALL products
    // This takes precedence over individual product changes
    if (yearlyChanged) {
      console.log('Billing cycle changed, updating all products');

      // For each active product in the subscription, we need to replace it with its new billing cycle version
      if (products.review && reviewItem) {
        changes.push({
          removeItemId: reviewItem.id,
          addPriceId: isYearly ? PriceId.SIMPLY_REVIEW_YEARLY : PriceId.SIMPLY_REVIEW_MONTHLY,
        });
      } else if (products.review && !reviewItem) {
        // Adding review while changing billing cycle
        changes.push({
          addPriceId: isYearly ? PriceId.SIMPLY_REVIEW_YEARLY : PriceId.SIMPLY_REVIEW_MONTHLY,
        });
      } else if (!products.review && reviewItem) {
        // Removing review while changing billing cycle
        changes.push({
          removeItemId: reviewItem.id,
        });
      }

      if (products.leads && leadsItem) {
        changes.push({
          removeItemId: leadsItem.id,
          addPriceId: isYearly
            ? PriceId.SIMPLY_LEADS_YEARLY
            : PriceId.SIMPLY_LEADS_MONTHLY_EARLYBIRD,
        });
      } else if (products.leads && !leadsItem) {
        // Adding leads while changing billing cycle
        changes.push({
          addPriceId: isYearly
            ? PriceId.SIMPLY_LEADS_YEARLY
            : PriceId.SIMPLY_LEADS_MONTHLY_EARLYBIRD,
        });
      } else if (!products.leads && leadsItem) {
        // Removing leads while changing billing cycle
        changes.push({
          removeItemId: leadsItem.id,
        });
      }
    } else {
      // No billing cycle change, handle product additions/removals

      // Review product changes
      if (reviewChanged) {
        if (products.review) {
          // Adding review
          changes.push({
            addPriceId: isYearly ? PriceId.SIMPLY_REVIEW_YEARLY : PriceId.SIMPLY_REVIEW_MONTHLY,
          });
        } else if (reviewItem) {
          // Removing review
          changes.push({
            removeItemId: reviewItem.id,
          });
        }
      }

      // Leads product changes
      if (leadsChanged) {
        if (products.leads) {
          // Adding leads
          changes.push({
            addPriceId: isYearly
              ? PriceId.SIMPLY_LEADS_YEARLY
              : PriceId.SIMPLY_LEADS_MONTHLY_EARLYBIRD,
          });
        } else if (leadsItem) {
          // Removing leads
          changes.push({
            removeItemId: leadsItem.id,
          });
        }
      }
    }

    if (changes.length === 0) {
      toast.error('Failed to determine subscription change parameters.');
      return;
    }

    console.log('Changes to make:', changes);

    try {
      const functionRef = httpsCallable(Functions, 'settings');
      const response: any = await functionRef({
        request: 'STRIPE_PRORATION',
        subscriptionId: subscription.id,
        // Send all changes as an array to use the new API format
        changes: changes.map((change) => ({
          addPriceId: change.addPriceId,
          removeItemId: change.removeItemId,
        })),
      });
      const error = response?.data?.httpErrorCode?.status || response?.data?.errorInfo?.code;

      if (error) {
        toast.error(
          response?.data?.errorInfo?.message || response?.data?.httpErrorCode?.canonicalName
        );
      } else {
        setProrationDetails(response.data);
        setIsConfirming(true);
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to fetch proration preview.');
    } finally {
      setIsUpdating(false);
    }
  };

  const openCustomerPortal = async () => {
    if (!customer?.stripeId) {
      toast.error('Customer information not available');
      return;
    }

    setIsOpeningPortal(true);

    try {
      const functionRef = httpsCallable(Functions, 'settings');
      const response: any = await functionRef({
        request: 'STRIPE_CREATE_PORTAL_LINK',
        // customerId: customer.stripeId,
        // @ts-ignore
        toltReferral: window?.tolt_referral as string,
        // The URL to return to after managing the subscription
        returnUrl: window.location.href,
      });

      const { url } = response.data;

      if (url) {
        // Redirect to the Stripe Customer Portal
        window.location.href = url;
      } else {
        toast.error('Failed to create customer portal link');
      }
    } catch (error: any) {
      console.error('Error opening customer portal:', error);
      toast.error(error?.message || 'Failed to open customer portal');
    } finally {
      setIsOpeningPortal(false);
    }
  };

  const createCheckoutPortal = async () => {
    // if (!customer?.stripeId) {
    //   toast.error('Customer information not available');
    //   return;
    // }

    setIsOpeningPortal(true);

    try {
      const reviewItem = products.review
        ? isYearly
          ? PriceId.SIMPLY_REVIEW_YEARLY
          : PriceId.SIMPLY_REVIEW_MONTHLY
        : null;
      const leadsItem = products.leads
        ? isYearly
          ? PriceId.SIMPLY_LEADS_YEARLY
          : PriceId.SIMPLY_LEADS_MONTHLY_EARLYBIRD
        : null;

      if (!reviewItem && !leadsItem) {
        toast.error('No products selected');
        return;
      }

      const priceIds: PriceIdType[] = [reviewItem, leadsItem].filter(
        (id) => id !== null
      ) as PriceIdType[];

      const functionRef = httpsCallable(Functions, 'settings');
      const response: any = await functionRef({
        request: 'STRIPE_CREATE_CHECKOUT_LINK',
        workspaceId: userId, // user id in this case is workspace id
        priceIds,
        // The URL to return to after managing the subscription
        returnUrl: window.location.href,
      });

      const { url } = response.data;

      if (url) {
        // Redirect to the Stripe Customer Portal
        window.location.href = url;
      } else {
        toast.error('Failed to create checkout link');
      }
    } catch (error: any) {
      console.error('Error creating checkout link:', error);
      toast.error(error?.message || 'Failed to create checkout link');
    } finally {
      setIsOpeningPortal(false);
    }
  };

  const handleConfirmProration = async () => {
    if (!subscription || !customer || !prorationDetails) {
      toast.error('Missing subscription information');
      return;
    }

    setIsUpdating(true);

    try {
      // Calculate changes again using the same logic from getProrationPreview
      const yearlyChanged = isYearly !== initialState.current.isYearly;
      const reviewChanged = products.review !== initialState.current.products.review;
      const leadsChanged = products.leads !== initialState.current.products.leads;

      const subItems = subscription.items || [];
      const changes: Array<{ addPriceId?: string; removeItemId?: string }> = [];

      // Find the current product items
      const reviewItem = subItems.find((item: any) =>
        [PriceId.SIMPLY_REVIEW_MONTHLY, PriceId.SIMPLY_REVIEW_YEARLY].includes(item.price.id)
      );

      const leadsItem = subItems.find((item: any) =>
        [PriceId.SIMPLY_LEADS_MONTHLY_EARLYBIRD, PriceId.SIMPLY_LEADS_YEARLY].includes(
          item.price.id
        )
      );

      // Use the same change detection logic as in getProrationPreview
      if (yearlyChanged) {
        // Update all existing products to match the new billing cycle
        if (products.review && reviewItem) {
          changes.push({
            removeItemId: reviewItem.id,
            addPriceId: isYearly ? PriceId.SIMPLY_REVIEW_YEARLY : PriceId.SIMPLY_REVIEW_MONTHLY,
          });
        } else if (products.review && !reviewItem) {
          changes.push({
            addPriceId: isYearly ? PriceId.SIMPLY_REVIEW_YEARLY : PriceId.SIMPLY_REVIEW_MONTHLY,
          });
        } else if (!products.review && reviewItem) {
          changes.push({
            removeItemId: reviewItem.id,
          });
        }

        if (products.leads && leadsItem) {
          changes.push({
            removeItemId: leadsItem.id,
            addPriceId: isYearly
              ? PriceId.SIMPLY_LEADS_YEARLY
              : PriceId.SIMPLY_LEADS_MONTHLY_EARLYBIRD,
          });
        } else if (products.leads && !leadsItem) {
          changes.push({
            addPriceId: isYearly
              ? PriceId.SIMPLY_LEADS_YEARLY
              : PriceId.SIMPLY_LEADS_MONTHLY_EARLYBIRD,
          });
        } else if (!products.leads && leadsItem) {
          changes.push({
            removeItemId: leadsItem.id,
          });
        }
      } else {
        // No billing cycle change, just handle product additions/removals
        if (reviewChanged) {
          if (products.review) {
            changes.push({
              addPriceId: isYearly ? PriceId.SIMPLY_REVIEW_YEARLY : PriceId.SIMPLY_REVIEW_MONTHLY,
            });
          } else if (reviewItem) {
            changes.push({
              removeItemId: reviewItem.id,
            });
          }
        }

        if (leadsChanged) {
          if (products.leads) {
            changes.push({
              addPriceId: isYearly
                ? PriceId.SIMPLY_LEADS_YEARLY
                : PriceId.SIMPLY_LEADS_MONTHLY_EARLYBIRD,
            });
          } else if (leadsItem) {
            changes.push({
              removeItemId: leadsItem.id,
            });
          }
        }
      }

      const functionRef = httpsCallable(Functions, 'settings');
      const response: any = await functionRef({
        request: 'STRIPE_UPDATE_SUBSCRIPTION',
        subscriptionId: subscription.id,
        changes: changes.map((change) => ({
          addPriceId: change.addPriceId,
          removeItemId: change.removeItemId,
        })),
      });

      const error = response?.data?.httpErrorCode?.status || response?.data?.errorInfo?.code;

      if (error) {
        toast.error(
          response?.data?.errorInfo?.message || response?.data?.httpErrorCode?.canonicalName
        );
      } else {
        toast.success('Subscription updated successfully!');
        // Reset local state to match new subscription (will be updated on next render via useEffect)
        setIsConfirming(false);
        setProrationDetails(null);
        // added to make sure state is fully reset
        window.location.reload();
      }
    } catch (error: any) {
      console.error('Error updating subscription:', error);
      toast.error(error?.message || 'Failed to update subscription');
    } finally {
      setIsUpdating(false);
    }
  };

  console.log('subscription', subscription);

  const hasInvalidLeadPriceIds = subscription?.items?.every(
    (item) =>
      !validLeadPriceIds.includes(item.price.id as PriceIdType) &&
      item.plan.product === simplyLeads &&
      subscription.status === 'active'
  );
  const hasInvalidReviewPriceIds = subscription?.items?.every(
    (item) =>
      !validReviewPriceIds.includes(item.price.id as PriceIdType) &&
      item.plan.product === simplyReview &&
      subscription.status === 'active'
  );

  const hasInvalidProduct = subscription?.items?.some(
    (item) => !validProducts.includes(item.plan.product) && subscription.status === 'active'
  );

  return (
    <>
      <ProrationConfirmationDialog
        isOpen={isConfirming}
        onClose={() => setIsConfirming(false)}
        onConfirm={handleConfirmProration}
        prorationDetails={prorationDetails}
        isUpdating={isUpdating}
      />
      <Card className="sm:max-w-[480px] p-8 bg-white dark:bg-black rounded-2xl shadow-xl relative">
        <CardHeader className="text-center">
          {hasInvalidLeadPriceIds && (
            <Alert className="bg-red-100 text-red-600 border-red-600">
              Seems like you are on an old version of SimplyLeads. You can still manage/cancel your
              subscription here. And add other products to it. But to update SimplyLeads, please
              contact our support.
            </Alert>
          )}
          {hasInvalidReviewPriceIds && (
            <Alert className="bg-red-100 text-red-600 border-red-600">
              Seems like you are on an old version of SimplyReview. You can still manage/cancel your
              subscription here. And add other products to it. But to update SimplyReview, please
              contact our support.
            </Alert>
          )}
          {hasInvalidProduct && (
            <Alert className="bg-red-100 text-red-600 border-red-600">
              Seems like you using a custom product. You can still manage/cancel your subscription
              here. But to update it further please contact our support.
            </Alert>
          )}
          <div className="text-xs text-muted-foreground absolute bottom-2 left-0 right-0">
            ID: {subscription?.id}
          </div>
          <CardTitle className="text-2xl font-bold">Simply Subscription</CardTitle>
          <CardDescription className="text-muted-foreground">
            Select which Simply products you want to subscribe to and proceed to Stripe payment
          </CardDescription>
        </CardHeader>

        <div className="flex items-center justify-center space-x-4 my-6">
          <Label htmlFor="billing-cycle" className="font-medium">
            Billed monthly
          </Label>
          <Switch id="billing-cycle" checked={isYearly} onCheckedChange={setIsYearly} />
          <div className="flex flex-col items-start">
            <Label htmlFor="billing-cycle" className="font-medium">
              Billed yearly
            </Label>
            <span className="text-xs text-green-600">Save up to 22%</span>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex justify-between items-center text-sm text-muted-foreground">
            <span>Products</span>
            <span>Select</span>
          </div>

          <div className="space-y-2">
            {!hasBundleProduct && (
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center gap-4">
                  <div
                    className="p-2 bg-black text-white rounded-lg"
                    style={{ backgroundColor: REVIEW.darker }}
                  >
                    <div className="rounded  h-6 w-6 items-center justify-center flex">
                      <SimplyReviewIcon width={12} color={REVIEW.light} />
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="font-medium">SimplyReview</span>
                    <Tooltip>
                      <TooltipTrigger>
                        <Info className="h-4 w-4 text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>
                          SimplyReview is a review platform that helps you generate reviews for your
                          business.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <span className="font-semibold text-blue-600">
                    €{reviewPrice}/{isYearly ? 'yr' : 'mo'}.
                  </span>
                  {hasInvalidReviewPriceIds ? (
                    <Checkbox checked disabled className="h-5 w-5" />
                  ) : (
                    <Checkbox
                      checked={products.review}
                      disabled={hasInvalidReviewPriceIds}
                      onCheckedChange={() => handleProductChange('review')}
                      className="h-5 w-5"
                    />
                  )}
                </div>
              </div>
            )}
            {/* Leads */}
            {!hasBundleProduct && (
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center gap-4">
                  <div
                    className="p-2 bg-black text-white rounded-lg"
                    style={{ backgroundColor: LEADS.darker }}
                  >
                    <div className="rounded h-6 w-6 items-center justify-center flex">
                      <SimplyLeadsIcon width={18} color={LEADS.light} />
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="font-medium">SimplyLeads</span>
                    <Tooltip>
                      <TooltipTrigger>
                        <Info className="h-4 w-4 text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>
                          SimplyLeads is a lead generation platform that helps you generate leads
                          for your business.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <span className="font-semibold text-blue-600">
                    €{leadsPrice}/{isYearly ? 'yr' : 'mo'}.
                  </span>
                  {hasInvalidLeadPriceIds ? (
                    <Checkbox checked disabled className="h-5 w-5" />
                  ) : (
                    <Checkbox
                      checked={products.leads}
                      disabled={hasInvalidLeadPriceIds}
                      onCheckedChange={() => handleProductChange('leads')}
                      className="h-5 w-5"
                    />
                  )}
                </div>
              </div>
            )}
            {/* Bundle */}
            {hasBundleProduct && (
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center gap-4">
                  <div
                    className="p-2 bg-black text-white rounded-lg"
                    style={{ backgroundColor: LABS.darker }}
                  >
                    <div className="rounded h-6 w-6 items-center justify-center flex">
                      <SimplyLeadsIcon width={18} color={LABS.light} />
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="font-medium">SimplyBundle</span>
                    <Tooltip>
                      <TooltipTrigger>
                        <Info className="h-4 w-4 text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>SimplyBundle is a bundle of all SimplyProducts.</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <span className="font-semibold text-blue-600">
                    €{bundlePrice}/{isYearly ? 'yr' : 'mo'}.
                  </span>
                  <Checkbox
                    checked={hasBundleProduct}
                    disabled
                    // onCheckedChange={() => handleProductChange('bundle')}
                    className="h-5 w-5"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        <Separator className="my-6" />

        <div className="space-y-2 text-right text-sm">
          {/* <div className="flex justify-between">
          <span className="text-muted-foreground">Price</span>
          <span>
            €{subtotal.toFixed(2)}/{isYearly ? 'yr' : 'mo'}.
          </span>
        </div> */}
          {/* <div className="flex justify-between">
          <span className="text-muted-foreground">VAT (25%)</span>
          <span>
            €{vat.toFixed(2)}/{isYearly ? 'yr' : 'mo'}.
          </span>
        </div> */}
          <div className="flex justify-between font-bold text-lg">
            <span className="text-blue-600">Total</span>
            <span className="text-blue-600">
              €{subtotal.toFixed(2)}/{isYearly ? 'yr' : 'mo'}.
            </span>
          </div>
          {/* {!isYearly && <p className="text-xs text-muted-foreground">€30/mo. starting July 1st</p>} */}
        </div>

        <CardFooter className="flex flex-col gap-2 mt-6">
          {(!subscription || subscription?.status === StripeSubscriptionStatus.Canceled) && (
            <Button
              className="w-full bg-gray-900 text-white hover:bg-gray-800 h-12 text-base"
              disabled={!hasProductsSelected || !hasChanges}
              onClick={createCheckoutPortal}
              loading={isOpeningPortal}
            >
              {'Checkout'}
            </Button>
          )}
          {!hasBundleProduct &&
            subscription &&
            subscription?.status !== StripeSubscriptionStatus.Canceled && (
              <Button
                className="w-full bg-gray-900 text-white hover:bg-gray-800 h-12 text-base"
                disabled={!hasProductsSelected || !hasChanges}
                onClick={getProrationPreview}
                loading={isUpdating || isOpeningPortal}
              >
                {hasChanges ? 'Update' : 'Checkout'}
              </Button>
            )}

          {subscription?.status !== StripeSubscriptionStatus.Canceled && (
            <Button
              variant="outline"
              className="w-full h-12 text-base mt-2"
              onClick={openCustomerPortal}
              loading={isOpeningPortal}
            >
              Manage/Cancel Subscription
            </Button>
          )}
        </CardFooter>
      </Card>
    </>
  );
}

// Helper to format currency
const formatCurrency = (amount: number, currency: string) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(amount / 100);

// Proration Confirmation Dialog Component
function ProrationConfirmationDialog({
  isUpdating,
  isOpen,
  onClose,
  onConfirm,
  prorationDetails,
}: {
  isUpdating: boolean;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  prorationDetails: any;
}) {
  if (!prorationDetails) return null;

  const { amountDue, currency, lineItems, prorationDate } = prorationDetails;

  // Separate prorated and recurring items
  const proratedItems = lineItems.filter((item: any) => item.proration === true);
  const recurringItems = lineItems.filter((item: any) => item.proration !== true);

  // Calculate total immediate proration amount (could be positive or negative)
  const proratedTotal = proratedItems.reduce((sum: number, item: any) => sum + item.amount, 0);

  // Format the date in a more readable format
  const effectiveDate = new Date(prorationDate * 1000).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Subscription Change Preview</DialogTitle>
          <DialogDescription>
            Please review your subscription changes effective {effectiveDate}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 my-4">
          {/* Immediate charges section */}
          <div className="space-y-2">
            <h4 className="font-semibold text-sm uppercase text-muted-foreground">
              Immediate Charges
            </h4>
            <div className="font-bold text-lg mb-1">{formatCurrency(proratedTotal, currency)}</div>
            {proratedItems.length > 0 ? (
              <div className="space-y-1 text-sm">
                {proratedItems.map((item: any, index: number) => (
                  <div key={`prorate-${index}`} className="flex justify-between">
                    <span>{item.desc}</span>
                    <span className="font-mono">{formatCurrency(item.amount, currency)}</span>
                  </div>
                ))}
                <div className="text-xs text-muted-foreground mt-1">
                  These are one-time prorated amounts for the current billing period.
                </div>
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">No immediate charges.</div>
            )}
          </div>

          <Separator />

          {/* Future recurring charges section */}
          <div className="space-y-2">
            <h4 className="font-semibold text-sm uppercase text-muted-foreground">
              Future Recurring Charges
            </h4>
            {recurringItems.length > 0 ? (
              <div className="space-y-1 text-sm">
                {recurringItems.map((item: any, index: number) => (
                  <div key={`recur-${index}`} className="flex justify-between">
                    <span>{item.desc}</span>
                    <span className="font-mono">{formatCurrency(item.amount, currency)}</span>
                  </div>
                ))}
                <div className="text-xs text-muted-foreground mt-1">
                  This is what you'll be charged on your next regular billing date.
                </div>
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">No recurring charges.</div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={onConfirm} loading={isUpdating}>
            Confirm Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
